
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, FileText, Download } from 'lucide-react';

interface CSVFileInputProps {
  id: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string | null;
  disabled?: boolean;
  description?: string;
  templateUrl?: string;
  templateFileName?: string;
  file: File | null;
  showExport?: boolean;
  onExport?: () => void;
  validateOnExport?: boolean;
  validateFileForUploadOnly?: boolean;
}

export const CSVFileInput: React.FC<CSVFileInputProps> = ({
  id,
  onChange,
  error,
  disabled,
  description,
  templateUrl,
  templateFileName,
  file,
  showExport = false,
  onExport,
  validateOnExport = true,
  validateFileForUploadOnly = false
}) => {
  return (
    <div className="space-y-4">
      {templateUrl && (
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {showExport ? 'Export/import data:' : 'Download template:'}
            </p>
            <div className="flex gap-2">
              {showExport && onExport && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onExport}
                  disabled={validateOnExport && !file && disabled}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export Current Data
                </Button>
              )}
              {!showExport && (
                <Button variant="outline" size="sm" asChild>
                  <a href={templateUrl} download={templateFileName || 'template.csv'}>
                    <Download className="mr-2 h-4 w-4" />
                    CSV Template
                  </a>
                </Button>
              )}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            <p>Tips for preparing your CSV:</p>
            <ul className="list-disc pl-4 pt-1 space-y-1">
              <li>Ensure your file has the required headers</li>
              <li>Use quotes for values containing commas (e.g. "Company, Inc.")</li>
              <li>Multiple values in a single column should be separated by semicolons</li>
              <li>Save as UTF-8 encoded CSV</li>
            </ul>
          </div>
        </div>
      )}
      
      <div className="grid gap-2">
        <label
          htmlFor={id}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          CSV File
        </label>
        
        <div className="relative">
          <Input
            id={id}
            type="file"
            accept=".csv"
            onChange={onChange}
            disabled={disabled}
            className="file:border-0"
          />
        </div>
        
        {file && (
          <div className="text-sm text-muted-foreground flex items-center">
            <FileText className="h-4 w-4 mr-2" />
            {file.name} ({Math.round(file.size / 1024)} KB)
          </div>
        )}
        
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      
      {(!validateFileForUploadOnly || file) && error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
